import * as React from "react";

import Text from "@src/lib/ui/Text";
import { useInlinePlainText } from "@src/lib/ui/UnstyledRichTextPreview";
import RichTextEditor from "@src/features/libraryItem/course/RichTextEditor";
import TiptapEditorContextProvider from "@src/features/libraryItem/course/contexts/TiptapEditorContext";
import { cn } from "@src/lib/utils/cn";
import { findPreviewPrefix } from "./utils";

export type PostCardVariant = "sent" | "scheduled" | "draft";

const claimClick =
  <E extends Element>(
    handler?: React.MouseEventHandler<E>,
  ): React.MouseEventHandler<E> =>
  (event) => {
    event.stopPropagation();
    handler?.(event);
  };

const PostCard = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { onCardClick?: () => void }
>(({ className, onCardClick, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex w-full max-w-[600px] flex-col overflow-hidden rounded-2xl border border-gray-2 bg-white",
      onCardClick && "cursor-pointer",
      className,
    )}
    {...props}
    role={onCardClick ? "button" : undefined}
    tabIndex={onCardClick ? 0 : undefined}
    onClick={
      onCardClick
        ? (e) => {
            if (e.currentTarget.contains(e.target as Node)) onCardClick();
          }
        : undefined
    }
    onKeyDown={
      onCardClick
        ? (e) => {
            if (e.target !== e.currentTarget) return;
            if (e.key === "Enter") onCardClick();
          }
        : undefined
    }
  />
));
PostCard.displayName = "PostCard";

const PostCardContent = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={cn("flex flex-col gap-1 px-3 py-4", className)}
    {...props}
  />
));
PostCardContent.displayName = "PostCardContent";

const PostCardFooter = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & { hideTopBorder?: boolean }
>(({ className, hideTopBorder, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      "flex items-center px-3 py-2",
      !hideTopBorder && "border-t border-gray-2",
      className,
    )}
    {...props}
  />
));
PostCardFooter.displayName = "PostCardFooter";

type PostActivityButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement> & {
  icon: React.ReactNode;
};

const PostActivityButton = React.forwardRef<
  HTMLButtonElement,
  PostActivityButtonProps
>(({ icon, children, className, onClick, ...props }, ref) => (
  <button
    ref={ref}
    type="button"
    className={cn("flex h-9 items-center gap-1.5 rounded-lg px-2.5", className)}
    onClick={claimClick(onClick)}
    {...props}
  >
    {icon}
    <Text variant="compact-title" className="text-gray-12">
      {children}
    </Text>
  </button>
));
PostActivityButton.displayName = "PostActivityButton";

const PostCardBody: React.FC<{
  messageText: string;
  onExpand?: () => void;
}> = ({ messageText, onExpand }) => {
  const [expanded, setExpanded] = React.useState(false);
  const [prefix, setPrefix] = React.useState<string | null>(null);
  const bodyRef = React.useRef<HTMLDivElement>(null);
  const text = useInlinePlainText(messageText);

  React.useLayoutEffect(() => {
    const element = bodyRef.current;
    if (!element || expanded) return;
    const measure = () => setPrefix(findPreviewPrefix(element, text));
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(element);
    return () => observer.disconnect();
  }, [text, expanded]);

  if (expanded) {
    return (
      <TiptapEditorContextProvider>
        <RichTextEditor
          text={messageText}
          disabled
          hasEditContentPermission={false}
          enableMentions
          className="m-0 text-[14px] leading-[20px] tracking-[-0.006em] text-gray-12"
        />
      </TiptapEditorContextProvider>
    );
  }

  return (
    <div
      ref={bodyRef}
      className="text-[14px] leading-[20px] font-normal tracking-[-0.006em] break-words text-gray-12"
    >
      {prefix === null ? (
        text
      ) : (
        <>
          {`${prefix}… `}
          <button
            type="button"
            className="font-semibold hover:underline"
            onClick={(e) => {
              e.stopPropagation();
              setExpanded(true);
              onExpand?.();
            }}
          >
            Show more
          </button>
        </>
      )}
    </div>
  );
};

export {
  PostCard,
  PostCardContent,
  PostCardFooter,
  PostActivityButton,
  PostCardBody,
  claimClick,
};
